import { Controller, useForm } from 'react-hook-form';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { Title, Description } from '@radix-ui/react-dialog';
import React, { useState, useEffect, useMemo } from 'react';

import { Button, Dialog, DialogCloseButton } from '$partials/ui';
import { getGlobalQuery } from '$utilities/global-data';
import { Checkbox, FloatingLabelInput, FloatingLabelTextarea, PhoneInput, Select } from '$partials/fields';
import { useAnalytics } from '$contexts/analytics';
import { useRecaptcha } from '$hooks/use-recaptcha';
import IconArrow from '$partials/icons/icon-arrow';
import IconClose from '$partials/icons/icon-close';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global').GeneralEnquiryDialog}
 */
const GeneralEnquiryDialog = ({
    isOpen,
    closeDialog,
    defaultOffice,
    disclaimerText: disclaimerTextProp
}) => {
    const { allOffices, allMarkets, enquiryForm } = getGlobalQuery();
    const { getTrackingData, createSourceSummary } = useAnalytics();
    const { getToken: getRecaptchaToken } = useRecaptcha();

    const disclaimerText = disclaimerTextProp || enquiryForm?.disclaimerText;

    const { control, register, handleSubmit, formState: { errors, isDirty, isSubmitting }, watch, getValues, setValue } = useForm();

    const regionValue = watch('region');
    const officeValue = watch('office');
    const enquiryTypeValue = watch('enquiryType');
    const [formError, setFormError] = useState();
    const [completed, setCompleted] = useState(false);

    // Filter available office to selected market (region)
    const availableOffices = useMemo(() => {
        if (regionValue) {
            return allOffices.filter(o => o.visibleInForms).filter(o => o.market?.title === regionValue);
        } else {
            return allOffices.filter(o => o.visibleInForms);
        }
    }, [regionValue]);

    const availableMarkets = useMemo(() => {
        return allMarkets.filter(m => allOffices.filter(o => o.visibleInForms).some(o => o.market?.title === m.title));
    }, [allMarkets]);

    const officeLabel = useMemo(() => {
        return allOffices.find(o => o.id === officeValue)?.title;
    }, [officeValue]);

    // Set market when office is selected and market is not set.
    useEffect(() => {
        if (officeValue && !getValues('region')) {
            const office = availableOffices.find(o => o.id === officeValue);
            if (office.market?.title) {
                setValue('region', office.market.title);
            }
        }
    }, [officeValue]);

    useEffect(() => {
        if (availableOffices.length === 1) {
            // console.log(availableOffices);
            setValue('office', availableOffices[0].id);
        }
    }, [availableOffices]);

    // Initialize default region based on default office
    useEffect(() => {
        if (defaultOffice?.id && !getValues('region')) {
            const office = availableOffices.find(o => o.id === defaultOffice.id);
            if (office.market?.title) {
                setValue('region', office.market.title);
            }
        }
    }, [defaultOffice?.id]);

    const handleSubmission = async (formData) => {
        setFormError(null);
        try {

            const trackingData = getTrackingData();
            const recaptchaToken = await getRecaptchaToken('general_enquiry');

            const response = await fetch('/api/v1/general-enquiry', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    body: formData.body,
                    email: formData.email,
                    enquiryType: formData.enquiryType,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    newsletter: formData.newsletter,
                    office: formData.office ?? defaultOffice?.id,
                    phone: formData.phone,
                    recaptchaToken,
                    region: formData.region,
                    utm: createSourceSummary(trackingData),
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.message);
            } else {
                setCompleted(true);
            }
        } catch (err) {
            setFormError(err?.message ?? err);
        }
    };

    return (
        <Dialog
            className='theme-light enquire-dialog'
            onOpenChange={(open) => {
                if (!open) { closeDialog(); }
            }}
            open={isOpen}
            warnBeforeClose={isDirty ? 'Are you sure you want to close this dialog?' : undefined}>
            {/* Modal Panel */}
            <div className='p-16 sm:px-32 sm:py-60 text-charcoal theme-light'>
                <DialogCloseButton className='button-primary-filled absolute z-20 top-16 right-16 flex select-none h-[40px] w-[40px] md:h-[44px] md:w-[44px] rounded justify-center items-center button-secondary-filled'>
                    <IconClose />
                </DialogCloseButton>

                {!completed && (
                    <>
                        {/* Heading */}
                        <header className='mb-32'>
                            <Title className='type-style-2 text-main-text-primary'>
                                Get in Touch
                            </Title>
                            <Description className='sr-only'>
                                Complete the form to make an enquiry
                            </Description>
                        </header>
                    </>
                )}

                {/* Contact Form */}
                <div className={`flex flex-col ${completed ? 'sr-only' : ''}`}>
                    <form
                        className='flex flex-col gap-18'
                        data-gtm-form='global_enquiry_form'
                        onSubmit={handleSubmit(handleSubmission)}>
                        {/* Form Sections */}
                        <div className='flex flex-col gap-32'>
                            {/* Your Details Section */}
                            <div className='flex flex-col gap-8'>
                                <p className='type-style-form-body-r text-main-text-secondary'>
                                    {'Your Details'}
                                </p>

                                {/* First Row: First Name + Last Name */}
                                <div className='flex flex-col sm:flex-row gap-8'>
                                    <div className='flex-1'>
                                        <FloatingLabelInput
                                            id='firstName'
                                            label='First name*'
                                            type='text'
                                            {...register('firstName', { required: 'First name is required' })} />
                                        {errors.firstName && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.firstName.message)}
                                            </p>
                                        )}
                                    </div>

                                    <div className='flex-1'>
                                        <FloatingLabelInput
                                            id='lastName'
                                            label='Last name*'
                                            type='text'
                                            {...register('lastName', { required: 'Last name is required' })} />
                                        {errors.lastName && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.lastName.message)}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Second Row: Email + Phone */}
                                <div className='flex flex-col sm:flex-row gap-8'>
                                    <div className='flex-1'>
                                        <FloatingLabelInput
                                            id='email'
                                            label='Email*'
                                            type='email'
                                            {...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: 'Invalid email address'
                                                }
                                            })} />
                                        {errors.email && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.email.message)}
                                            </p>
                                        )}
                                    </div>

                                    <div className='flex-1 relative phone-input'>
                                        <label
                                            className='sr-only'
                                            htmlFor='Phone'>
                                            {'Phone*'}
                                        </label>
                                        <PhoneInput
                                            control={control}
                                            id='Phone'
                                            name='phone'
                                            placeholder='Phone number'
                                            rules={{
                                                required: 'Phone number is required',
                                                validate: (value) => {
                                                    return !isPossiblePhoneNumber(value) ? 'Enter a valid phone number' : null;
                                                }
                                            }} />
                                        {errors.phone && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.phone.message)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Your Enquiry Section */}
                            <div className='flex flex-col gap-8'>
                                <p className='type-style-form-body-r text-main-text-secondary'>
                                    {'Your Enquiry'}
                                </p>

                                {/* Enquiry Type Dropdown */}
                                <div className='relative'>
                                    <Controller
                                        control={control}
                                        name='enquiryType'
                                        render={({ field: { onChange, value, name} }) => {
                                            const options = [
                                                {
                                                    label: 'General Enquiry',
                                                    value: 'general'
                                                },
                                                {
                                                    label: 'Buying a Property',
                                                    value: 'buying'
                                                },
                                                {
                                                    label: 'Selling a Property',
                                                    value: 'selling'
                                                },
                                                {
                                                    label: 'Renting a Property',
                                                    value: 'renting'
                                                },
                                                {
                                                    label: 'Letting a Property',
                                                    value: 'letting'
                                                },
                                            ];

                                            return (
                                                <Select
                                                    displayValue={value ? options.find((opt) => opt.value === value)?.label : 'Enquiry type*'}
                                                    name={name}
                                                    onChange={onChange}
                                                    options={options}
                                                    triggerClassName='w-full h-[52px] md:h-[44px] md:h-[44px] px-12 py-10 input-background rounded text-input-label type-style-eyebrow appearance-none placeholder:text-input-label'
                                                    value={value} />
                                            );
                                        }}
                                        rules={{ required: 'Enquiry type is required' }} />
                                    {errors.enquiryType && (
                                        <p className='text-system-fail text-sm mt-4'>
                                            {String(errors.enquiryType.message)}
                                        </p>
                                    )}
                                </div>

                                {/* Region + Office Row */}
                                <div className='flex flex-col sm:flex-row gap-8'>
                                    <div className='flex-1 relative'>
                                        {availableMarkets?.length > 0 && (
                                            <>
                                                <Controller
                                                    control={control}
                                                    name='region'
                                                    render={({ field: { onChange, value, name } }) => {
                                                        const options = availableMarkets.map((market) => {
                                                            return {
                                                                label: market.title,
                                                                value: market.title
                                                            };
                                                        });

                                                        return (
                                                            <Select
                                                                displayValue={value ? options.find((opt) => opt.value === value)?.label : 'Region*'}
                                                                name={name}
                                                                onChange={onChange}
                                                                options={options}
                                                                triggerClassName='w-full h-[52px] md:h-[44px] md:h-[44px] px-12 py-10 input-background rounded text-input-label type-style-eyebrow appearance-none placeholder:text-input-label'
                                                                value={value} />
                                                        );
                                                    }}
                                                    rules={{ required: 'Region is required' }} />

                                                {errors.region && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.region.message)}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className='flex-1 relative'>
                                        {availableOffices?.length > 0 && (
                                            <>
                                                <Controller
                                                    control={control}
                                                    defaultValue={defaultOffice?.id}
                                                    name='office'
                                                    render={({ field: { onChange, value, name } }) => {
                                                        const options = availableOffices.map((office) => {
                                                            return {
                                                                label: office.title,
                                                                value: office.id
                                                            };
                                                        });

                                                        return (
                                                            <Select
                                                                displayValue={value ? options.find((opt) => opt.value === value)?.label : 'Office*'}
                                                                name={name}
                                                                onChange={onChange}
                                                                options={options}
                                                                triggerClassName='w-full h-[52px] md:h-[44px] md:h-[44px] px-12 py-10 input-background rounded text-input-label type-style-eyebrow appearance-none placeholder:text-input-label'
                                                                value={value} />
                                                        );
                                                    }}
                                                    rules={{ required: 'Office is required' }} />

                                                {errors.office && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.office.message)}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <FloatingLabelTextarea
                                    id='enquiry'
                                    label='Message'
                                    type='text'
                                    {...register('body')} />
                                {errors.body && (
                                    <p className='text-system-fail text-sm mt-4'>
                                        {String(errors.body.message)}
                                    </p>
                                )}

                                <div className='flex gap-12 items-center'>
                                    <Controller
                                        control={control}
                                        name='termsAccepted'
                                        render={({ field: { onChange, value, name } }) => {
                                            return (
                                                <Checkbox
                                                    checked={value}
                                                    id='termsAccepted'
                                                    name={name}
                                                    onCheckedChange={onChange} />
                                            );
                                        }}
                                        rules={{
                                            required: 'You must accept the terms and conditions'
                                        }} />
                                    <label
                                        className='type-style-caption text-main-text-secondary'
                                        htmlFor='termsAccepted'>
                                        {'I agree to Terms of Use'}
                                    </label>
                                </div>
                                {errors.termsAccepted && (
                                    <p className='text-system-fail text-sm mt-4'>
                                        {String(errors.termsAccepted.message)}
                                    </p>
                                )}

                                <div className='flex gap-12 items-center'>
                                    <Controller
                                        control={control}
                                        name='newsletter'
                                        render={({ field: { onChange, value, name } }) => {
                                            return (
                                                <Checkbox
                                                    checked={value}
                                                    id='newsletter'
                                                    name={name}
                                                    onCheckedChange={onChange} />
                                            );
                                        }} />
                                    <label
                                        className='type-style-caption text-main-text-secondary'
                                        htmlFor='newsletter'>
                                        {'I would like to receive the Beauchamp Estates newsletter'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Button Row with Disclaimer */}
                        <div className='flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-y-12 pt-20 sm:pt-12'>
                            {disclaimerText && (
                                <div
                                    className='disclaimer-rich-text type-style-caption text-main-text-secondary max-w-[365px]'
                                    dangerouslySetInnerHTML={{ __html: disclaimerText }} />
                            )}
                            <Button
                                className='button-brand-filled w-[82px] py-13 px-16 type-style-eyebrow'
                                type='submit'
                                disabled={isSubmitting}>
                                {'Submit'}
                            </Button>
                        </div>
                        {formError && (
                            <p
                                className='text-system-fail text-sm mt-4'
                                role='alert'>
                                {formError}
                            </p>
                        )}
                    </form>
                </div>


                {!completed && (
                    <>
                        {/* Our Offices Section */}
                        {allOffices?.length > 0 && (
                            <div className='flex flex-col gap-12 mt-48'>
                                <h3 className='type-style-4 text-main-text-primary'>
                                    {'Our Offices'}
                                </h3>
                                <div className='flex flex-wrap gap-x-[22px] gap-y-12'>
                                    {allOffices?.map((office, index) => {
                                        return (
                                            <a
                                                className='flex items-center gap-2 w-[256px] type-style-form-body-r text-main-text-primary'
                                                href={office?.relativeUrl}
                                                key={office.id || index}>
                                                <span>{office?.title}</span>
                                                <IconArrow className='ml-10 h-[10px]'/>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Completed screen */}
                {completed && (
                    <div
                        className='h-full flex flex-col justify-center sm:justify-normal'
                        data-gtm-success='global_form_success'>
                        <div>
                            <p className='type-style-3 mb-12'>
                                {'Thank You for Your Enquiry'}
                            </p>
                            <p className='type-style-body-r mb-16'>
                                {`Your ${enquiryTypeValue} enquiry for ${regionValue} — ${officeLabel} has been successfully submitted. One of our team members will be in touch soon.`}
                            </p>
                            <Button
                                className='button-primary-filled flex select-none h-[40px] md:h-[44px] px-16 rounded justify-center items-center bg-button-secondary-filled-background'
                                onClick={closeDialog}>
                                {'Close'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default GeneralEnquiryDialog;
